import React, { useMemo, useState } from 'react';
import { Briefcase, Building, Factory, GraduationCap, HeartPulse, Home, LayoutTemplate, PencilRuler, Sparkles, Trophy } from 'lucide-react';

import axiosInstance from 'src/common/axiosInstance';
import { StageDialog, HelpTip } from 'src/features/Stage/shared/StageKit';
import { apiMessage, stageTones } from 'src/features/Stage/shared/crmWork';
import { FIELD_TYPES } from '../LeadFields/tableBlueprint';
import { PIPELINE_TEMPLATES, templatePayload } from './pipelineTemplates';
import './pipeline-templates.css';

// AUTO-CRM-32: "Create pipeline" starts here. Pick a ready-made pipeline (stages,
// steps and a lead table) and name it, or open the full designer.
const ICONS = { sales: Briefcase, home: Home, education: GraduationCap, agency: Building, clinic: HeartPulse, b2b: Factory };
const clash = (error) => /already exists/i.test(apiMessage(error, ''));

export default function PipelineTemplateGallery({ onClose, onCreated, onCustom }) {
  const [selectedId, setSelectedId] = useState(PIPELINE_TEMPLATES[0].id);
  const template = PIPELINE_TEMPLATES.find((entry) => entry.id === selectedId);
  const [name, setName] = useState(template.name);
  const [nameTouched, setNameTouched] = useState(false);
  // The server makes the first pipeline the main one on its own.
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const tones = useMemo(() => stageTones(template.stages.map((stage, index) => ({ id: index, is_closed: Boolean(stage.closed), probability: stage.probability }))), [template]);
  const fields = template.sections.flatMap((section) => section.fields);

  const pick = (entry) => {
    setSelectedId(entry.id);
    if (!nameTouched) setName(entry.name);
    setError('');
  };

  const create = async () => {
    if (!name.trim()) { setError('Give your pipeline a name.'); return; }
    setBusy(true);
    setError('');
    try {
      let response;
      // Field keys are unique per workspace: retry with numbered keys when this
      // template was used before. A failed import creates nothing.
      for (let attempt = 1; attempt <= 4; attempt += 1) {
        try {
          response = await axiosInstance.post('/admin/pipelines/import', templatePayload(template, { name, isDefault, keySuffix: attempt === 1 ? '' : String(attempt) }));
          break;
        } catch (requestError) {
          if (attempt === 4 || !clash(requestError)) throw requestError;
        }
      }
      const data = response?.data?.data || {};
      onCreated?.(data.pipeline || data, template);
    } catch (requestError) {
      setError(apiMessage(requestError, 'Couldn’t create the pipeline. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <StageDialog
      title="Create a pipeline"
      subtitle="Start from a ready-made pipeline for your kind of business. You can rename, add or remove anything later."
      icon={LayoutTemplate}
      width={980}
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          {error ? <span className="crm-kit-error" role="alert">{error}</span> : <span className="ws-grow ws-muted">Creates {template.stages.length} stages, {template.stages.reduce((count, stage) => count + stage.steps.length, 0)} steps and a {fields.length}-field lead table.</span>}
          <button type="button" className="ws-btn ws-btn--quiet" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="ws-btn ws-btn--primary" onClick={create} disabled={busy}><Sparkles size={15} />{busy ? 'Creating…' : 'Create pipeline'}</button>
        </>
      )}
    >
      <div className="crm-tpl">
        <div className="crm-tpl-list" role="radiogroup" aria-label="Templates">
          {PIPELINE_TEMPLATES.map((entry) => {
            const Icon = ICONS[entry.icon] || Briefcase;
            const on = entry.id === selectedId;
            return (
              <button key={entry.id} type="button" role="radio" aria-checked={on} className={`crm-tpl-card${on ? ' is-on' : ''}`} onClick={() => pick(entry)} disabled={busy}>
                <span className="crm-tpl-icon" aria-hidden="true"><Icon size={17} /></span>
                <span className="ws-grow">
                  <b>{entry.name}</b>
                  <small>{entry.tagline}</small>
                </span>
              </button>
            );
          })}
          <button type="button" className="crm-tpl-card crm-tpl-card--custom" onClick={onCustom} disabled={busy}>
            <span className="crm-tpl-icon" aria-hidden="true"><PencilRuler size={17} /></span>
            <span className="ws-grow">
              <b>Design my own</b>
              <small>Build the lead table yourself or import a sheet</small>
            </span>
          </button>
        </div>

        <div className="crm-tpl-preview" aria-live="polite">
          <p className="crm-tpl-desc">{template.description}</p>

          <section className="crm-kit-section">
            <span className="crm-kit-label"><HelpTip term="stage">Stages</HelpTip> leads move through</span>
            <ol className="crm-tpl-stages">
              {template.stages.map((stage, index) => (
                <li key={stage.name} style={{ '--tone': tones[index]?.color }} className={stage.closed ? `is-${stage.probability >= 100 ? 'won' : 'lost'}` : ''}>
                  <span className="crm-tpl-stage-head">
                    <i aria-hidden="true" />
                    <b>{stage.name}</b>
                    {stage.closed ? (stage.probability >= 100 ? <Trophy size={12} aria-label="Won" /> : <small>closed</small>) : <small>{stage.probability}%</small>}
                  </span>
                  <ul>{stage.steps.map((step) => <li key={step}>{step}</li>)}</ul>
                </li>
              ))}
            </ol>
            <p className="ws-muted crm-kit-hint">The % is how likely a lead at that stage is to buy. It feeds your forecast in Insights.</p>
          </section>

          <section className="crm-kit-section">
            <span className="crm-kit-label">What you’ll record for each lead (<HelpTip term="leadtable">lead table</HelpTip>)</span>
            <div className="crm-tpl-fields">
              <span className="crm-tpl-field is-fixed">Name</span>
              {fields.map((item) => (
                <span key={item.key} className="crm-tpl-field" title={FIELD_TYPES[item.type]}>
                  {item.label}{item.required && <em aria-label="required">*</em>}
                  {item.options?.length ? <small>{item.options.slice(0, 3).join(' · ')}{item.options.length > 3 ? '…' : ''}</small> : null}
                </span>
              ))}
            </div>
          </section>

          <div className="crm-kit-grid crm-tpl-name">
            <label className="crm-kit-section">
              <span className="crm-kit-label">Pipeline name</span>
              <input className="ws-input crm-kit-input" value={name} maxLength={100} onChange={(event) => { setName(event.target.value); setNameTouched(true); setError(''); }} disabled={busy} placeholder="e.g. Website enquiries" />
            </label>
            <label className="crm-kit-check crm-tpl-default">
              <input type="checkbox" checked={isDefault} onChange={(event) => setIsDefault(event.target.checked)} disabled={busy} />
              <span>Make this the main pipeline<span className="ws-muted"> · new leads go here by default</span></span>
            </label>
          </div>
        </div>
      </div>
    </StageDialog>
  );
}
